import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Structured editor for the assessment journey pages stored in
// ProductRegionConfig.metadata (results_page + report_page). Generic template —
// any assessment product can use it; ADHD is the first. Every field is
// optional: blank fields are omitted so the dashboard's built-in defaults
// apply. The dashboard reads these via the resolve payload (resultsPage /
// reportPage) and pulls prices live from the referenced bundle/product.

export interface AssessmentPagesValue {
  results_page: Record<string, any> | null;
  report_page: Record<string, any> | null;
}

// ── Per-form copy layers ────────────────────────────────────────────────────
// A page container is either LEGACY FLAT (the config keys sit at the top level,
// as authored before this feature) or FORM-SCOPED once it carries a `by_form`
// map: `{ default_form, by_form: { [formId]: {…overrides} } }`. The editor shows
// and edits ONE form's layer at a time (top-right "Editing copy for form"
// dropdown); the server later collapses by_form → a flat object per user using
// the form they submitted, so the dashboard never sees this shape.

type PageContainer = Record<string, any> | null;

const isFormScoped = (c: PageContainer): boolean =>
  !!c && typeof c === "object" && !!c.by_form && typeof c.by_form === "object";

// The flat config layer currently being edited for `formKey`. A legacy flat
// container shows under the default form only (until first edited → migrated).
const layerOf = (
  c: PageContainer,
  formKey: string,
  defaultFormId: string,
): Record<string, any> => {
  if (!c) return {};
  if (isFormScoped(c)) return (c.by_form as Record<string, any>)[formKey] ?? {};
  const { default_form, by_form: _by, ...flat } = c;
  return formKey === (default_form ?? defaultFormId) ? flat : {};
};

// A new container with `formKey`'s layer replaced (migrating a legacy flat
// container into by_form on first edit, attributing its copy to the default form).
const withLayer = (
  c: PageContainer,
  formKey: string,
  layer: Record<string, any>,
  defaultFormId: string,
): Record<string, any> => {
  if (isFormScoped(c)) {
    return { ...c, by_form: { ...(c!.by_form as object), [formKey]: layer } };
  }
  const dform = (c && c.default_form) || defaultFormId;
  const byForm: Record<string, any> = {};
  if (c) {
    const { default_form: _d, by_form: _b, ...flat } = c;
    if (Object.keys(flat).length) byForm[dform] = flat;
  }
  byForm[formKey] = layer;
  return { default_form: dform, by_form: byForm };
};

const OUTCOME_KEYS = [
  { key: "SIGNS_INDICATED", label: "Outcome: signs indicated" },
  { key: "NOT_INDICATED", label: "Outcome: not indicated" },
  { key: "PENDING", label: "Outcome: not recorded yet (fallback)" },
];

function Field({
  label,
  value,
  onChange,
  multiline,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  placeholder?: string;
}) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      {multiline ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? "Default copy applies when blank"}
          className="min-h-16 text-sm"
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder ?? "Default copy applies when blank"}
          className="h-9 text-sm"
        />
      )}
    </div>
  );
}

// Dynamic list of { title, body } rows (steps / info items).
function TitleBodyListField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: { title?: string; body?: string }[];
  onChange: (v: { title?: string; body?: string }[]) => void;
}) {
  const rows = value ?? [];
  const update = (i: number, patch: Record<string, string | undefined>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">{label}</p>
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1 space-y-1">
            <Input
              value={row.title ?? ""}
              onChange={(e) => update(i, { title: e.target.value })}
              placeholder="Title"
              className="h-8 text-sm"
            />
            <Textarea
              value={row.body ?? ""}
              onChange={(e) => update(i, { body: e.target.value })}
              placeholder="Body"
              className="min-h-12 text-sm"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, {}])}
      >
        <Plus className="h-3 w-3 mr-1" /> Add item
      </Button>
    </div>
  );
}

// Dynamic list of plain strings (check bullets).
function StringListField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const rows = value ?? [];
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">{label}</p>
      {rows.map((row, i) => (
        <div key={i} className="flex gap-2 items-center">
          <Input
            value={row}
            onChange={(e) =>
              onChange(rows.map((r, j) => (j === i ? e.target.value : r)))
            }
            className="h-8 text-sm flex-1"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...rows, ""])}
      >
        <Plus className="h-3 w-3 mr-1" /> Add bullet
      </Button>
    </div>
  );
}

const REPORT_VARIANTS = [
  { key: "ADHD_CONFIRMED", label: "Variant: ADHD confirmed" },
  { key: "NOT_INDICATED", label: "Variant: ADHD not indicated" },
];

// Per-outcome copy editor for the rich DIVA report page. Blank fields fall
// back to the dashboard's built-in defaults (the Figma copy).
function ReportVariantEditor({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Record<string, any>;
  onChange: (v: Record<string, any>) => void;
}) {
  const set = (section: string, patch: Record<string, any>) =>
    onChange({ ...value, [section]: { ...(value[section] ?? {}), ...patch } });
  return (
    <div className="space-y-3 border rounded-md p-3">
      <p className="text-xs font-semibold text-gray-700">{label}</p>
      <Field
        label="Verdict badge"
        value={value.verdict?.badge ?? ""}
        onChange={(v) => set("verdict", { badge: v || undefined })}
      />
      <Field
        label="Verdict headline"
        value={value.verdict?.title ?? ""}
        onChange={(v) => set("verdict", { title: v || undefined })}
      />
      <Field
        label="Verdict body"
        multiline
        value={value.verdict?.body ?? ""}
        onChange={(v) => set("verdict", { body: v || undefined })}
      />
      <TitleBodyListField
        label="Verdict stats (title = label, body = value; blank → defaults)"
        value={value.verdict?.stats ?? []}
        onChange={(v) =>
          set("verdict", {
            stats: v.length
              ? v.map((r) => ({ label: r.title, value: r.body }))
              : undefined,
          })
        }
      />
      <Field
        label="Download card title"
        value={value.download?.title ?? ""}
        onChange={(v) => set("download", { title: v || undefined })}
      />
      <Field
        label="Download card body"
        multiline
        value={value.download?.body ?? ""}
        onChange={(v) => set("download", { body: v || undefined })}
      />
      <Field
        label="Download button label"
        value={value.download?.cta ?? ""}
        onChange={(v) => set("download", { cta: v || undefined })}
      />
      <Field
        label="Handoff badge"
        value={value.handoff?.badge ?? ""}
        onChange={(v) => set("handoff", { badge: v || undefined })}
      />
      <Field
        label="Handoff title"
        value={value.handoff?.title ?? ""}
        onChange={(v) => set("handoff", { title: v || undefined })}
      />
      <Field
        label="Handoff body"
        multiline
        value={value.handoff?.body ?? ""}
        onChange={(v) => set("handoff", { body: v || undefined })}
      />
      <Field
        label="Handoff chip title"
        value={value.handoff?.chip_title ?? ""}
        onChange={(v) => set("handoff", { chip_title: v || undefined })}
      />
      <Field
        label="Handoff chip subtitle"
        value={value.handoff?.chip_subtitle ?? ""}
        onChange={(v) => set("handoff", { chip_subtitle: v || undefined })}
      />
      <Field
        label="Primary button label"
        value={value.cta?.primary ?? ""}
        onChange={(v) => set("cta", { primary: v || undefined })}
      />
      <Field
        label="Secondary button label"
        value={value.cta?.secondary ?? ""}
        onChange={(v) => set("cta", { secondary: v || undefined })}
      />
    </div>
  );
}

// Top-right controls for a form-scoped page: pick which form's copy you're
// editing, and which form's copy fills blanks (the fallback/default).
function FormScopeControls({
  forms,
  editing,
  onEditing,
  fallback,
  onFallback,
}: {
  forms: { id: string; name: string }[];
  editing: string;
  onEditing: (v: string) => void;
  fallback: string;
  onFallback: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-end gap-3">
      <div>
        <p className="text-xs text-gray-500 mb-1">Editing copy for form</p>
        <Select value={editing} onValueChange={onEditing}>
          <SelectTrigger className="h-9 w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {forms.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
                {f.id === fallback ? " — default" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <p className="text-xs text-gray-500 mb-1">Default form (fills blanks)</p>
        <Select value={fallback} onValueChange={onFallback}>
          <SelectTrigger className="h-9 w-52">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {forms.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export function AssessmentPagesField({
  value,
  onChange,
  bundles,
  products,
  forms,
}: {
  value: AssessmentPagesValue;
  onChange: (v: AssessmentPagesValue) => void;
  /** This product's bundles (the diagnostic offer target). */
  bundles: { standard_id: string; name: string }[];
  /** All products (the onward-journey target, e.g. Clinical Therapy). */
  products: { id: string; name: string }[];
  /** The product's onboarding forms — drives the per-form copy dropdowns.
   *  Empty → the editor stays a single flat config (no per-form UI). */
  forms: { id: string; name: string }[];
}) {
  const perForm = forms.length > 0;
  const defaultFormFallback = forms[0]?.id ?? "";

  const resultsContainer = value.results_page;
  const reportContainer = value.report_page;

  // Which form's layer is being edited (UI-only; not persisted).
  const [resultsForm, setResultsForm] = useState<string>("");
  const [reportForm, setReportForm] = useState<string>("");

  // The persisted fallback form per page (fills a form's blank fields).
  const resultsDefaultForm =
    (resultsContainer && resultsContainer.default_form) || defaultFormFallback;
  const reportDefaultForm =
    (reportContainer && reportContainer.default_form) || defaultFormFallback;

  // Clamp the editing selection to a real form; default to the fallback form.
  const activeResultsForm =
    perForm && forms.some((f) => f.id === resultsForm)
      ? resultsForm
      : resultsDefaultForm;
  const activeReportForm =
    perForm && forms.some((f) => f.id === reportForm)
      ? reportForm
      : reportDefaultForm;

  const results: Record<string, any> | null = !resultsContainer
    ? null
    : perForm
      ? layerOf(resultsContainer, activeResultsForm, resultsDefaultForm)
      : resultsContainer;
  const report: Record<string, any> | null = !reportContainer
    ? null
    : perForm
      ? layerOf(reportContainer, activeReportForm, reportDefaultForm)
      : reportContainer;

  const setResults = (patch: Record<string, any>) => {
    const layer = { ...(results ?? {}), ...patch };
    onChange({
      ...value,
      results_page: perForm
        ? withLayer(
            resultsContainer,
            activeResultsForm,
            layer,
            resultsDefaultForm,
          )
        : layer,
    });
  };
  const setReport = (patch: Record<string, any>) => {
    const layer = { ...(report ?? {}), ...patch };
    onChange({
      ...value,
      report_page: perForm
        ? withLayer(reportContainer, activeReportForm, layer, reportDefaultForm)
        : layer,
    });
  };

  // Persist the fallback-form choice, ensuring the container is form-scoped.
  const setResultsDefaultForm = (formId: string) =>
    onChange({
      ...value,
      results_page: {
        ...withLayer(
          resultsContainer,
          activeResultsForm,
          results ?? {},
          resultsDefaultForm,
        ),
        default_form: formId,
      },
    });
  const setReportDefaultForm = (formId: string) =>
    onChange({
      ...value,
      report_page: {
        ...withLayer(
          reportContainer,
          activeReportForm,
          report ?? {},
          reportDefaultForm,
        ),
        default_form: formId,
      },
    });
  const setOutcome = (key: string, patch: Record<string, any>) =>
    setResults({
      outcomes: {
        ...((results?.outcomes as Record<string, any>) ?? {}),
        [key]: { ...((results?.outcomes?.[key] as object) ?? {}), ...patch },
      },
    });
  const setDiva = (patch: Record<string, any>) =>
    setResults({ diva_offer: { ...(results?.diva_offer ?? {}), ...patch } });
  const setOverview = (patch: Record<string, any>) =>
    setDiva({
      overview: { ...(results?.diva_offer?.overview ?? {}), ...patch },
    });
  const overview = results?.diva_offer?.overview ?? {};
  const setTherapy = (patch: Record<string, any>) =>
    setResults({
      therapy_offer: { ...(results?.therapy_offer ?? {}), ...patch },
    });

  return (
    <div className="space-y-6">
      {/* ── Results page ── */}
      <div className="flex items-start justify-between gap-4">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={!!resultsContainer}
            onCheckedChange={(c) =>
              onChange({
                ...value,
                results_page: c
                  ? (resultsContainer ??
                    (perForm
                      ? { default_form: defaultFormFallback, by_form: {} }
                      : {}))
                  : null,
              })
            }
          />
          <span className="text-sm font-medium">
            Results page (shown when the assessment completes without a
            diagnostic)
          </span>
        </label>
        {resultsContainer && perForm && (
          <FormScopeControls
            forms={forms}
            editing={activeResultsForm}
            onEditing={setResultsForm}
            fallback={resultsDefaultForm}
            onFallback={setResultsDefaultForm}
          />
        )}
      </div>

      {results && (
        <div className="space-y-4 border-l-2 border-gray-100 pl-4">
          <Field
            label="Eyebrow (small label above the title)"
            value={results.eyebrow ?? ""}
            onChange={(v) => setResults({ eyebrow: v || undefined })}
          />
          <Field
            label="Title"
            value={results.title ?? ""}
            onChange={(v) => setResults({ title: v || undefined })}
          />
          <Field
            label="Subtitle"
            multiline
            value={results.subtitle ?? ""}
            onChange={(v) => setResults({ subtitle: v || undefined })}
            placeholder="Blank → built from the assigned clinician's name"
          />
          <Field
            label="Product-page button label"
            value={results.cta_label ?? ""}
            onChange={(v) => setResults({ cta_label: v || undefined })}
            placeholder="Blank → 'View your results'"
          />

          {OUTCOME_KEYS.map(({ key, label }) => (
            <div key={key} className="space-y-2">
              <p className="text-xs font-semibold text-gray-700">{label}</p>
              <Field
                label="Badge (small label above the verdict)"
                value={results.outcomes?.[key]?.badge ?? ""}
                onChange={(v) => setOutcome(key, { badge: v || undefined })}
                placeholder="e.g. PSYCHOLOGIST'S READ"
              />
              <Field
                label="Headline"
                value={results.outcomes?.[key]?.title ?? ""}
                onChange={(v) => setOutcome(key, { title: v || undefined })}
              />
              <Field
                label="Body"
                multiline
                value={results.outcomes?.[key]?.body ?? ""}
                onChange={(v) => setOutcome(key, { body: v || undefined })}
              />
            </div>
          ))}

          {/* Diagnostic offer */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              Diagnostic offer (e.g. DIVA test) — price comes live from the
              selected bundle
            </p>
            <div>
              <p className="text-xs text-gray-500 mb-1">Offer bundle</p>
              <Select
                value={
                  results.diva_offer
                    ? (results.diva_offer.standard_id ?? "INCLUDED")
                    : "NONE"
                }
                onValueChange={(v) =>
                  v === "NONE"
                    ? setResults({ diva_offer: undefined })
                    : // "Included in the pack": keep the offer copy but with no
                      // separate purchasable bundle (e.g. PD's IPDE, already paid).
                      v === "INCLUDED"
                      ? setDiva({ standard_id: undefined })
                      : setDiva({ standard_id: v })
                }
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select the offer bundle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">No diagnostic offer</SelectItem>
                  <SelectItem value="INCLUDED">
                    Included in the pack (no separate bundle)
                  </SelectItem>
                  {bundles.map((b) => (
                    <SelectItem key={b.standard_id} value={b.standard_id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Copy shows whenever an offer exists — with a bundle (priced buy
                offer) OR "Included in the pack" (no bundle). The dashboard renders
                this copy in both cases, so the admin must too. */}
            {results.diva_offer && (
              <>
                {bundles.length > 0 && (
                  <div className="space-y-1 border-l-2 border-gray-100 pl-4">
                    <p className="text-xs font-semibold text-gray-700">
                      Hide this offer if the user already owns…
                    </p>
                    <p className="text-xs text-gray-500">
                      Tick the bundles that already include this test. If the
                      user owns any of them, the "Continue your journey" offer
                      is hidden for them (e.g. IPDE bought separately).
                    </p>
                    {bundles.map((b) => {
                      const owned: string[] =
                        results.diva_offer?.hide_if_owned ?? [];
                      return (
                        <label
                          key={b.standard_id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            checked={owned.includes(b.standard_id)}
                            onCheckedChange={(c) => {
                              const next = c
                                ? [...owned, b.standard_id]
                                : owned.filter((id) => id !== b.standard_id);
                              setDiva({
                                hide_if_owned: next.length ? next : undefined,
                              });
                            }}
                          />
                          <span className="text-xs text-gray-600">
                            {b.name}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <Field
                  label="Payment page slug"
                  value={results.diva_offer?.payment_slug ?? ""}
                  onChange={(v) => setDiva({ payment_slug: v || undefined })}
                  placeholder="e.g. adhd-diva"
                />
                <Field
                  label="Price note (next to the amount)"
                  value={results.diva_offer?.price_note ?? ""}
                  onChange={(v) => setDiva({ price_note: v || undefined })}
                  placeholder="e.g. test + 30-min follow-up"
                />
                <Field
                  label="Card title"
                  value={results.diva_offer?.title ?? ""}
                  onChange={(v) => setDiva({ title: v || undefined })}
                />
                <Field
                  label="Card body"
                  multiline
                  value={results.diva_offer?.body ?? ""}
                  onChange={(v) => setDiva({ body: v || undefined })}
                />
                <Field
                  label="Button label (buy variant)"
                  value={results.diva_offer?.cta ?? ""}
                  onChange={(v) => setDiva({ cta: v || undefined })}
                />
                <Field
                  label="Tagline (small line above the card title)"
                  value={results.diva_offer?.tagline ?? ""}
                  onChange={(v) => setDiva({ tagline: v || undefined })}
                  placeholder="e.g. IPDE test + results session"
                />
                <p className="text-xs font-semibold text-gray-700 pt-1">
                  Included-in-the-pack variant (already paid — no extra payment)
                </p>
                <Field
                  label="Included note (replaces the price line)"
                  value={results.diva_offer?.included_note ?? ""}
                  onChange={(v) => setDiva({ included_note: v || undefined })}
                  placeholder="e.g. test + 30-min follow-up · no extra payment"
                />
                <Field
                  label="Included button label"
                  value={results.diva_offer?.included_cta ?? ""}
                  onChange={(v) => setDiva({ included_cta: v || undefined })}
                  placeholder="e.g. Schedule your next session"
                />
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={results.diva_offer?.recommended !== false}
                    onCheckedChange={(c) => setDiva({ recommended: !!c })}
                  />
                  <span className="text-xs text-gray-600">
                    Show the RECOMMENDED badge
                  </span>
                </label>

                {/* Overview page (intermediary before payment). Checked (the
                    default) → the buy flow shows this page; unchecked → it skips
                    straight to payment. */}
                <label className="flex items-center gap-2 pt-2">
                  <Checkbox
                    checked={results.diva_offer?.show_overview !== false}
                    onCheckedChange={(c) =>
                      setDiva({ show_overview: c ? undefined : false })
                    }
                  />
                  <span className="text-xs font-semibold text-gray-700">
                    Show the overview page before payment (uncheck to skip
                    straight to payment; defaults apply to blank fields)
                  </span>
                </label>
                {results.diva_offer?.show_overview !== false && (
                  <div className="space-y-3 border-l-2 border-gray-100 pl-4">
                    <Field
                      label="Title"
                      value={overview.title ?? ""}
                      onChange={(v) => setOverview({ title: v || undefined })}
                    />
                    <Field
                      label="Subtitle"
                      multiline
                      value={overview.subtitle ?? ""}
                      onChange={(v) =>
                        setOverview({ subtitle: v || undefined })
                      }
                    />
                    <Field
                      label="Hero image URL"
                      value={overview.image ?? ""}
                      onChange={(v) => setOverview({ image: v || undefined })}
                      placeholder="Blank → no image"
                    />
                    <TitleBodyListField
                      label="Steps (blank list → defaults)"
                      value={overview.steps ?? []}
                      onChange={(v) =>
                        setOverview({ steps: v.length ? v : undefined })
                      }
                    />
                    <Field
                      label="Buy button label"
                      value={overview.cta ?? ""}
                      onChange={(v) => setOverview({ cta: v || undefined })}
                    />
                    <Field
                      label="'Why recommended' — title"
                      value={overview.why?.title ?? ""}
                      onChange={(v) =>
                        setOverview({
                          why: {
                            ...(overview.why ?? {}),
                            title: v || undefined,
                          },
                        })
                      }
                    />
                    <Field
                      label="'Why recommended' — intro"
                      multiline
                      value={overview.why?.intro ?? ""}
                      onChange={(v) =>
                        setOverview({
                          why: {
                            ...(overview.why ?? {}),
                            intro: v || undefined,
                          },
                        })
                      }
                    />
                    <StringListField
                      label="'Why recommended' — check bullets (blank → defaults)"
                      value={overview.why?.bullets ?? []}
                      onChange={(v) =>
                        setOverview({
                          why: {
                            ...(overview.why ?? {}),
                            bullets: v.length ? v : undefined,
                          },
                        })
                      }
                    />
                  </div>
                )}

                {/* "What is…" explainer — rendered on the RESULTS page (not
                    just the overview page), so it stays editable even when the
                    overview page is hidden. */}
                <div className="space-y-3 border-l-2 border-gray-100 pl-4">
                  <p className="text-xs font-semibold text-gray-700">
                    "What is…" explainer (shown on the results page)
                  </p>
                  <Field
                    label="'What is' — title"
                    value={overview.what_is?.title ?? ""}
                    onChange={(v) =>
                      setOverview({
                        what_is: {
                          ...(overview.what_is ?? {}),
                          title: v || undefined,
                        },
                      })
                    }
                  />
                  <Field
                    label="'What is' — subtitle"
                    value={overview.what_is?.subtitle ?? ""}
                    onChange={(v) =>
                      setOverview({
                        what_is: {
                          ...(overview.what_is ?? {}),
                          subtitle: v || undefined,
                        },
                      })
                    }
                  />
                  <Field
                    label="'What is' — intro"
                    multiline
                    value={overview.what_is?.intro ?? ""}
                    onChange={(v) =>
                      setOverview({
                        what_is: {
                          ...(overview.what_is ?? {}),
                          intro: v || undefined,
                        },
                      })
                    }
                  />
                  <TitleBodyListField
                    label="'What is' — info items (blank → defaults)"
                    value={overview.what_is?.items ?? []}
                    onChange={(v) =>
                      setOverview({
                        what_is: {
                          ...(overview.what_is ?? {}),
                          items: v.length ? v : undefined,
                        },
                      })
                    }
                  />
                </div>
              </>
            )}
          </div>

          {/* Onward journey */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-700">
              Onward journey (e.g. Clinical Therapy) — "from" price comes live
              from the selected product's packs
            </p>
            <Select
              value={results.therapy_offer?.product_id ?? "NONE"}
              onValueChange={(v) =>
                v === "NONE"
                  ? setResults({ therapy_offer: undefined })
                  : setTherapy({ product_id: v })
              }
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select the onward product" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">No onward offer</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {results.therapy_offer?.product_id && (
              <>
                <Field
                  label="Card title"
                  value={results.therapy_offer?.title ?? ""}
                  onChange={(v) => setTherapy({ title: v || undefined })}
                />
                <Field
                  label="Card body"
                  multiline
                  value={results.therapy_offer?.body ?? ""}
                  onChange={(v) => setTherapy({ body: v || undefined })}
                />
                <Field
                  label="Button label"
                  value={results.therapy_offer?.cta ?? ""}
                  onChange={(v) => setTherapy({ cta: v || undefined })}
                />
              </>
            )}
          </div>

          <Field
            label="Next-step section title"
            value={results.next_step?.title ?? ""}
            onChange={(v) =>
              setResults({
                next_step: { ...(results.next_step ?? {}), title: v || undefined },
              })
            }
          />
          <Field
            label="Next-step section subtitle"
            multiline
            value={results.next_step?.body ?? ""}
            onChange={(v) =>
              setResults({
                next_step: { ...(results.next_step ?? {}), body: v || undefined },
              })
            }
          />
          <Field
            label="Explainer title"
            value={results.explainer?.title ?? ""}
            onChange={(v) =>
              setResults({
                explainer: { ...(results.explainer ?? {}), title: v || undefined },
              })
            }
          />
          <Field
            label="Explainer body"
            multiline
            value={results.explainer?.body ?? ""}
            onChange={(v) =>
              setResults({
                explainer: { ...(results.explainer ?? {}), body: v || undefined },
              })
            }
          />
          <Field
            label="Footnote title"
            value={results.footnote?.title ?? ""}
            onChange={(v) =>
              setResults({
                footnote: { ...(results.footnote ?? {}), title: v || undefined },
              })
            }
          />
          <Field
            label="Footnote body"
            multiline
            value={results.footnote?.body ?? ""}
            onChange={(v) =>
              setResults({
                footnote: { ...(results.footnote ?? {}), body: v || undefined },
              })
            }
          />
        </div>
      )}

      {/* ── Report page ── */}
      <div className="flex items-start justify-between gap-4">
        <label className="flex items-center gap-2 ">
          <Checkbox
            checked={!!reportContainer}
            onCheckedChange={(c) =>
              onChange({
                ...value,
                report_page: c
                  ? (reportContainer ??
                    (perForm
                      ? { default_form: defaultFormFallback, by_form: {} }
                      : {}))
                  : null,
              })
            }
          />
          <span className="text-sm font-medium">
            Report page (after the diagnostic test, once the report PDF is
            saved)
          </span>
        </label>
        {reportContainer && perForm && (
          <FormScopeControls
            forms={forms}
            editing={activeReportForm}
            onEditing={setReportForm}
            fallback={reportDefaultForm}
            onFallback={setReportDefaultForm}
          />
        )}
      </div>

      {report && (
        <div className="space-y-4 border-l-2 border-gray-100 pl-4">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={report.hide_scores === true}
              onCheckedChange={(c) =>
                setReport({ hide_scores: c ? true : undefined })
              }
            />
            <span className="text-xs text-gray-600">
              Hide the score-breakdown section (the per-domain x/9 points block).
              Leave unchecked to show it — for assessments without those scores
              (e.g. IPDE / Personality Disorder).
            </span>
          </label>
          <Field
            label="Product-page button label (report saved)"
            value={report.product_cta_label ?? ""}
            onChange={(v) => setReport({ product_cta_label: v || undefined })}
            placeholder="Blank → 'View your report'"
          />
          <Field
            label="Ready — title"
            value={report.ready?.title ?? ""}
            onChange={(v) =>
              setReport({
                ready: { ...(report.ready ?? {}), title: v || undefined },
              })
            }
          />
          <Field
            label="Ready — body"
            multiline
            value={report.ready?.body ?? ""}
            onChange={(v) =>
              setReport({
                ready: { ...(report.ready ?? {}), body: v || undefined },
              })
            }
          />
          <Field
            label="Download button label"
            value={report.ready?.cta ?? ""}
            onChange={(v) =>
              setReport({
                ready: { ...(report.ready ?? {}), cta: v || undefined },
              })
            }
          />
          <Field
            label="Not ready — title"
            value={report.pending?.title ?? ""}
            onChange={(v) =>
              setReport({
                pending: { ...(report.pending ?? {}), title: v || undefined },
              })
            }
          />
          <Field
            label="Not ready — body"
            multiline
            value={report.pending?.body ?? ""}
            onChange={(v) =>
              setReport({
                pending: { ...(report.pending ?? {}), body: v || undefined },
              })
            }
          />
          <Field
            label="Page eyebrow (rich report)"
            value={report.eyebrow ?? ""}
            onChange={(v) => setReport({ eyebrow: v || undefined })}
          />
          <Field
            label="Page title (rich report)"
            value={report.title ?? ""}
            onChange={(v) => setReport({ title: v || undefined })}
          />
          <Field
            label="Page subtitle (rich report)"
            multiline
            value={report.subtitle ?? ""}
            onChange={(v) => setReport({ subtitle: v || undefined })}
          />
          {REPORT_VARIANTS.map(({ key, label }) => (
            <ReportVariantEditor
              key={key}
              label={label}
              value={report.variants?.[key] ?? {}}
              onChange={(v) =>
                setReport({
                  variants: { ...(report.variants ?? {}), [key]: v },
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
